import { Building2, Check, LayoutGrid, Loader2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { useListTenants } from '@/hooks/scoped/tenants/use-fetch-tenants';
import { cn } from '@/lib/utils';
import { pathToAppStart } from '@/navigation/navigate';
import { iconSize } from '@/theme/size-tokens';
import { flattenPages } from '@/types/shared/page';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

const ZERO_UUID = '00000000-0000-0000-0000-000000000000';

type SidebarTenantPickerProps = {
  collapsed: boolean;
};

export default function SidebarTenantPicker({
  collapsed,
}: SidebarTenantPickerProps) {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useListTenants({ limit: 50 });

  const tenants = flattenPages(data?.pages);
  const activeTenant = slug ? tenants.find((t) => t.slug === slug) : null;

  const lastPage = data?.pages?.length
    ? data.pages[data.pages.length - 1]
    : undefined;
  const lastNext = lastPage?.next ?? null;
  const showLoadMore = Boolean(
    hasNextPage && lastNext && lastNext !== ZERO_UUID
  );

  return (
    <div className="shrink-0">
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Switch tenant"
            className={cn(
              'flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-[colors,padding] duration-200',
              'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
              collapsed && 'justify-center gap-0'
            )}
          >
            <div className="bg-sidebar-accent text-sidebar-accent-foreground flex size-8 shrink-0 items-center justify-center rounded-full">
              {isLoading ? (
                <Loader2 className={cn(iconSize('sm'), 'animate-spin')} />
              ) : (
                <Building2 className={iconSize('sm')} />
              )}
            </div>
            <span
              className={cn(
                'flex-1 truncate overflow-hidden whitespace-nowrap text-sm transition-[opacity,max-width] duration-200',
                collapsed ? 'max-w-0 opacity-0' : 'max-w-40 opacity-100'
              )}
            >
              {activeTenant?.name ?? 'Select tenant'}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="right"
          align="end"
          sideOffset={8}
          className="w-64 p-0"
        >
          {/* Tenant list */}
          <div className="max-h-72 overflow-y-auto p-1">
            {tenants.map((tenant) => (
              <button
                key={tenant.id}
                type="button"
                className={cn(
                  'flex w-full items-center justify-start gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors',
                  tenant.slug === slug
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-accent hover:text-accent-foreground'
                )}
                onClick={() => navigate(`/${tenant.slug}`)}
              >
                <Building2 className={iconSize('sm')} />
                <span className="flex-1 truncate">{tenant.name}</span>
                {tenant.slug === slug && <Check className={iconSize('sm')} />}
              </button>
            ))}

            {showLoadMore && (
              <button
                type="button"
                disabled={isFetchingNextPage}
                className="text-muted-foreground hover:bg-accent hover:text-accent-foreground flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors disabled:opacity-50"
                onClick={() => fetchNextPage()}
              >
                {isFetchingNextPage ? (
                  <Loader2 className={cn(iconSize('sm'), 'animate-spin')} />
                ) : (
                  <Loader2 className={iconSize('sm')} />
                )}
                {isFetchingNextPage ? 'Loading…' : 'Load more'}
              </button>
            )}
          </div>

          <div className="border-t" />

          {/* All tenants link */}
          <div className="p-1">
            <button
              type="button"
              className="hover:bg-accent hover:text-accent-foreground flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors"
              onClick={() => navigate(pathToAppStart())}
            >
              <LayoutGrid className={iconSize('sm')} />
              All tenants
            </button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
